import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  REPORT_TYPES,
  eventAnalysesFor,
  eventFramesFor,
  needsScan,
  type EventAnalysisDef,
  type EventAttendee,
  type ReportFieldDef,
  type ReportListItem,
  type SubjectStatus,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import { useEventRunStateInvalidation, useWriteAnalysisReport } from '../../api/event-hooks';
import { useRun } from '../../api/hooks';
import { useReport, useReports } from '../../api/report-hooks';
import { RunBlock } from '../../components/run-strip/run-block';
import { ErrorLine } from '../../components/ui/error-line';
import { IfWrites } from '../../components/write-access';
import {
  EVENT_ANALYSIS_NOBODY,
  EVENT_ANALYSIS_NONE_OFFERED,
  EVENT_ANALYSIS_NO_LIST,
  EVENT_ANALYSIS_SCANNING,
  EVENT_ANALYSIS_TOTAL,
  EVENT_ANALYSIS_REPORT_FIELD_HINT,
  EVENT_ANALYSIS_REPORT_WRITING,
  EVENT_REPORT_HELD_READY,
  EVENT_REPORT_REFRESH,
  EVENT_REPORT_STALE,
  EVENT_REPORT_WRITE,
  EVENT_REPORT_WRITE_NOW,
  EVENT_REPORT_WRITE_STARTING,
  EVENT_ANALYSIS_INPUT_HINT,
  EVENT_ANALYSIS_INPUT_MAX,
  EVENT_NOT_TRACKED,
  EVENT_TRACKED,
  EVENT_SHOW_EVERY_GROUP,
  eventAnalysisRatedAgainst,
  eventAnalysisUnscannedLine,
  eventAttendeesDisclosure,
  eventDisclosureTracked,
  eventFilterLine,
  eventReportAction,
  eventTrackedConclusion,
  subjectStatusLabel,
} from '../../lib/copy';
import { attendeeDetails } from './attendees';
import { AttendeeModal } from './attendee-modal';
import { Legend, Ring, statusSegment, type ChartSegment } from './analysis-chart';
import {
  FactorGroups,
  FactorSummary,
  ReviewLine,
  framePalette,
  useWholeList,
  usePicks,
} from './analysis-factor';
import { statusesByTone } from './statuses';
import { useEventRecord } from './record';
import { ReportExcerpt } from './report-excerpt';
import { ScanButton } from './scan-control';
import { Loading } from '../../components/ui/loading';
import { WorkingButton } from '../../components/ui/working-button';
import { EmptyLine } from '../../components/ui/empty-line';
import { StatusDot } from '../../components/ui/status-word';
import './analysis.css';

export { tierTone, tilesOf } from './analysis-factor';

/**
 * **The Analysis tab** (docs/notes/2026-09-24-event-analysis-flow-spec.md; the
 * blend spec's "Analysis summaries"): the à la carte menu of analyses the
 * org's vertical offers (`eventAnalysesFor`), one tab each. Every analysis
 * leads with its summary, built from data that already exists: the scan's own
 * results where there is a scan, otherwise the attendee list and the stored
 * sections of its latest report. No analysis is only a button. The tab renders
 * the registry and knows nothing of any one vertical.
 */
export function EventAnalysisTab() {
  const { entityId } = useEventRecord();
  const vertical = useOrg().vertical;
  const analyses = eventAnalysesFor(vertical);
  const [params, setParams] = useSearchParams();
  const chosen = analyses.find((one) => one.key === params.get('analysis')) ?? analyses[0];
  if (!chosen) {
    return (
      <div className="section analysis">
        <EmptyLine>{EVENT_ANALYSIS_NONE_OFFERED}</EmptyLine>
      </div>
    );
  }

  return (
    <>
      <div className="subtabs" role="tablist" aria-label="Analyses">
        {analyses.map((one) => (
          <button
            key={one.key}
            type="button"
            role="tab"
            aria-selected={one.key === chosen.key}
            className={one.key === chosen.key ? 'subtab on' : 'subtab'}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('analysis', one.key);
              setParams(next, { replace: true });
            }}
          >
            {one.label}
          </button>
        ))}
      </div>
      {needsScan(chosen) ? (
        <ScanAnalysisPanel
          key={chosen.key}
          analysis={chosen}
          entityId={entityId}
          vertical={vertical}
        />
      ) : (
        <ReportAnalysisPanel key={chosen.key} analysis={chosen} entityId={entityId} />
      )}
    </>
  );
}

/** How long after a scan ends the tab expects its report to show. */
const REPORT_EXPECTED_WITHIN_MS = 30_000;

/**
 * The analysis's head: its name, and the way to write its report while it has
 * none. A scanned analysis's report is written with its scan (2026-10-03), so
 * it offers none.
 */
function AnalysisHead({
  analysis,
  entityId,
  latest,
  reportsLoaded,
}: {
  analysis: EventAnalysisDef;
  entityId: string;
  latest: ReportListItem | undefined;
  reportsLoaded: boolean;
}) {
  const { record } = useEventRecord();
  return (
    <>
      <div className="an-head">
        <h2>{analysis.label}</h2>
        {reportsLoaded && !latest && !needsScan(analysis) ? (
          <IfWrites>
            <Link
              className="btn act"
              to={newAnalysisReportPath(analysis, entityId, record.subject.account?.id ?? null)}
            >
              {eventReportAction(REPORT_TYPES[analysis.report].display_name)}
            </Link>
          </IfWrites>
        ) : null}
      </div>
      <p className="an-offer">{analysis.offer}</p>
    </>
  );
}

/**
 * An analysis rated by the attendee scan: its offer and the scan until a scan
 * has run on this list, then its summary: per factor the ring, conclusion,
 * legend and attention case (and for a risk factor, the seating), the
 * report's quotable sections, and everyone by group behind a disclosure.
 */
function ScanAnalysisPanel({
  analysis,
  entityId,
  vertical,
}: {
  analysis: EventAnalysisDef;
  entityId: string;
  vertical: string;
}) {
  const frames = eventFramesFor(vertical).filter((frame) => analysis.factors.includes(frame.key));
  /* Only a risk factor's seating draws the whole list. */
  const whole = useWholeList(
    entityId,
    (first) => first.counts.seated > 0 && frames.some((frame) => framePalette(frame) === 'risk'),
  );
  const { list } = whole;
  const head = list.data?.pages[0];
  const scan = head?.scan ?? null;
  /* A scan that completes while the tab is open has just started the report:
     read the list at the run's cadence until it shows, for a short while. */
  const [scanEndedAt, setScanEndedAt] = useState<number | null>(null);
  const activeId = scan?.active_run_id ?? null;
  const [wasActive, setWasActive] = useState(activeId);
  if (wasActive !== activeId) {
    setWasActive(activeId);
    if (wasActive !== null && activeId === null) setScanEndedAt(Date.now());
  }
  const [reportsSoon, setReportsSoon] = useState(false);
  const reports = useReports({ soon: reportsSoon });
  const latest = latestAnalysisReport(reports.data?.items ?? [], analysis, entityId);
  const soon =
    latest?.status === 'preparing' ||
    (scanEndedAt !== null && Date.now() - scanEndedAt < REPORT_EXPECTED_WITHIN_MS);
  if (soon !== reportsSoon) setReportsSoon(soon);
  const { pickOf, setPick } = usePicks();
  const [open, setOpen] = useState<string | null>(null);
  /* What the office enters for the analysis's parameterized factors, starting
     from the input its ratings show once there is one. */
  const shown = scan?.analyses.find((one) => one.analysis === analysis.key)?.input ?? null;
  const [edited, setInput] = useState<Record<string, string> | null>(null);
  const input = edited ?? shown ?? {};
  /* The report's own fields, asked with the scan that writes it, starting from
     what its report holds: a scan writes them over the report's. */
  const reportFields = REPORT_TYPES[analysis.report].fields;
  const preparing = latest?.status === 'preparing';
  /* Read whenever there is a report: its fields, its run while it is written,
     and whether the attendee list has moved on since. */
  const held = useReport(latest ? latest.id : null);
  /* The report is written by a run of its own once the scan ends: its strip
     takes over from the scan's, so the tab never drops to a line mid-write. */
  const reportRun = useRun(preparing ? (held.data?.active_run?.id ?? null) : null);
  const [editedReport, setReportInput] = useState<Record<string, string> | null>(null);
  const reportInput = editedReport ?? held.data?.inputs ?? {};
  const activeRun = useRun(scan?.active_run_id ?? null);
  useEventRunStateInvalidation(activeRun.data);

  // "Has run": a scan for this analysis exists on this list, whatever became of it.
  const own = scan?.analyses.find((one) => one.analysis === analysis.key);
  const ran = Boolean(own?.last_run_id);
  const reportHeld = Boolean(own?.report_held) && !scan?.active_run_id && !preparing;
  const stale = Boolean(held.data?.record_changed) && !preparing && !reportHeld;
  const writeReport = useWriteAnalysisReport(entityId, analysis.key);

  let body;
  if (list.isPending)
    body = (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  else if (list.isError) body = <ErrorLine error={list.error} load />;
  else if (!head || head.counts.total === 0) body = <EmptyLine>{EVENT_ANALYSIS_NO_LIST}</EmptyLine>;
  else {
    const running = Boolean(scan?.active_run_id);
    /* An analysis that takes the office's input rates everyone already scanned
       again for a new input, and nobody new for the one its ratings show. */
    const takesInput = analysis.inputs.length > 0;
    const last = own?.input ?? null;
    const entered = Object.fromEntries(
      analysis.inputs.map((one) => [one.key, (input[one.key] ?? '').trim()]),
    );
    const blank = takesInput && Object.values(entered).some((value) => value === '');
    const sameAsLast =
      last !== null && analysis.inputs.every((one) => entered[one.key] === (last[one.key] ?? ''));
    const searched = !running && scan ? scan.scannable : 0;
    const rated = !running && scan ? scan.record_rated : 0;
    const rerate =
      running || !own ? 0 : takesInput && !sameAsLast ? (own.rerate_new_input ?? 0) : own.rerate;
    const unscanned = searched + rated + rerate;
    const scanProps = {
      eventId: entityId,
      analysis: analysis.key,
      scannable: searched,
      recordRated: rated,
      rerate,
      input: entered,
      reportInputs: Object.fromEntries(
        reportFields.flatMap((one) => {
          const value = (reportInput[one.key] ?? '').trim();
          return value ? [[one.key, value]] : [];
        }),
      ),
      disabled: blank,
    };
    const inputs = takesInput ? (
      <AnalysisInputs analysis={analysis} values={input} onChange={setInput} last={last} />
    ) : null;
    const forReport =
      reportFields.length > 0 ? (
        <ReportFieldInputs fields={reportFields} values={reportInput} onChange={setReportInput} />
      ) : null;
    const seated = head.counts.seated;
    body = (
      <>
        {running && activeRun.data ? (
          <>
            <IfWrites>
              <RunBlock
                subjectId={entityId}
                run={activeRun.data}
                pendingDecisions={[]}
                pollFailures={activeRun.failureCount}
              />
            </IfWrites>
            {!ran ? <EmptyLine>{EVENT_ANALYSIS_SCANNING}</EmptyLine> : null}
          </>
        ) : preparing && reportRun.data ? (
          <IfWrites>
            <RunBlock
              subjectId={entityId}
              run={reportRun.data}
              pendingDecisions={[]}
              pollFailures={reportRun.failureCount}
            />
          </IfWrites>
        ) : null}
        {!ran && !running ? (
          <div className="analysis-run">
            <IfWrites>
              {inputs}
              {unscanned > 0 ? forReport : null}
              {unscanned > 0 ? <ScanButton {...scanProps} /> : null}
            </IfWrites>
          </div>
        ) : null}
        {ran ? (
          <>
            {head.counts.needs_review > 0 ? (
              <ReviewLine
                entityId={entityId}
                count={head.counts.needs_review}
                reportHeld={reportHeld}
                onOpen={setOpen}
              />
            ) : null}
            {/* The scan held its report while anyone was unmatched: written on
                this press, never on its own. */}
            {reportHeld ? (
              <IfWrites>
                <div className="an-report-held">
                  {head.counts.needs_review === 0 ? (
                    <p className="src">{EVENT_REPORT_HELD_READY}</p>
                  ) : null}
                  <WorkingButton
                    label={
                      head.counts.needs_review === 0 ? EVENT_REPORT_WRITE : EVENT_REPORT_WRITE_NOW
                    }
                    workingLabel={EVENT_REPORT_WRITE_STARTING}
                    working={writeReport.isPending}
                    variant={head.counts.needs_review === 0 ? 'go' : 'act'}
                    tiny={head.counts.needs_review > 0}
                    onClick={() => writeReport.mutate()}
                  />
                  {writeReport.isError ? <ErrorLine error={writeReport.error} /> : null}
                </div>
              </IfWrites>
            ) : null}
            {stale && latest ? (
              <div className="an-review" role="status">
                {EVENT_REPORT_STALE}{' '}
                <IfWrites>
                  <Link className="linkish flush" to={`/reports/${latest.id}?refresh=1`}>
                    {EVENT_REPORT_REFRESH}
                  </Link>
                </IfWrites>
              </div>
            ) : null}
            {takesInput && last ? <RatedAgainst analysis={analysis} input={last} /> : null}
            {unscanned > 0 ? (
              <div className="analysis-again">
                <p className="src">
                  {eventAnalysisUnscannedLine(
                    searched,
                    rated,
                    rerate,
                    takesInput && last !== null && !sameAsLast,
                  )}
                </p>
                <IfWrites>
                  {inputs}
                  {forReport}
                  <ScanButton {...scanProps} variant="act" />
                </IfWrites>
              </div>
            ) : takesInput ? (
              /* Nothing to scan: only a writer's box for a new text stands here. */
              <IfWrites>
                <div className="analysis-again">{inputs}</div>
              </IfWrites>
            ) : null}
            {frames.map((frame) => (
              <FactorSummary
                key={frame.key}
                entityId={entityId}
                frame={frame}
                counts={head.frames.find((one) => one.frame === frame.key)}
                seated={seated}
                everyone={whole.rows}
                pick={pickOf(frame.key)}
                onPick={(next) => setPick(frame.key, next)}
                onOpen={setOpen}
              />
            ))}
            {reports.isError ? <ErrorLine error={reports.error} load /> : null}
            {preparing ? (
              <p className="src" role="status">
                {EVENT_ANALYSIS_REPORT_WRITING}
              </p>
            ) : null}
            {latest ? <ReportExcerpt analysis={analysis} item={latest} /> : null}
            {frames.map((frame) => (
              <FactorGroups
                key={frame.key}
                entityId={entityId}
                frame={frame}
                counts={head.frames.find((one) => one.frame === frame.key)}
                seated={seated > 0}
                pick={pickOf(frame.key)}
                onPick={(next) => setPick(frame.key, next)}
                onOpen={setOpen}
                titled={frames.length > 1}
              />
            ))}
          </>
        ) : null}
      </>
    );
  }

  return (
    <section className="an-panel section analysis">
      <AnalysisHead
        analysis={analysis}
        entityId={entityId}
        latest={latest}
        reportsLoaded={reports.isSuccess}
      />
      {body}
      {open ? (
        <AttendeeModal eventId={entityId} nodeId={open} onClose={() => setOpen(null)} />
      ) : null}
    </section>
  );
}

/**
 * **What the office enters for an analysis's parameterized factors** (the
 * principal's objectives, what the office wants from the event): one text area
 * per input. The ratings are true only for what was entered, so a changed text
 * is a new scan that rates everyone already scanned again.
 */
function AnalysisInputs({
  analysis,
  values,
  onChange,
  last,
}: {
  analysis: EventAnalysisDef;
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  last: Record<string, string> | null;
}) {
  return (
    <div className="analysis-inputs">
      {analysis.inputs.map((one) => (
        <label className="field" key={one.key}>
          <span className="label">{one.label}</span>
          <textarea
            className="control"
            aria-label={one.label}
            rows={3}
            maxLength={EVENT_ANALYSIS_INPUT_MAX}
            placeholder={last ? undefined : EVENT_ANALYSIS_INPUT_HINT}
            value={values[one.key] ?? ''}
            onChange={(e) => onChange({ ...values, [one.key]: e.target.value })}
          />
        </label>
      ))}
    </div>
  );
}

/**
 * **The report's own fields, asked with the scan** (the crowd summary's
 * concern): the report is written when the scan completes, and these steer
 * only its write-up. Each is optional.
 */
function ReportFieldInputs({
  fields,
  values,
  onChange,
}: {
  fields: readonly ReportFieldDef[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  return (
    <div className="analysis-inputs">
      {fields.map((one) => (
        <label className="field" key={one.key}>
          <span className="label">{one.label}</span>
          <span className="an-field-note">
            {one.prompt} {EVENT_ANALYSIS_REPORT_FIELD_HINT}
          </span>
          <textarea
            className="control"
            aria-label={one.label}
            rows={2}
            maxLength={EVENT_ANALYSIS_INPUT_MAX}
            value={values[one.key] ?? ''}
            onChange={(e) => onChange({ ...values, [one.key]: e.target.value })}
          />
        </label>
      ))}
    </div>
  );
}

/** The input the ratings on show were made against, in the office's own words. */
function RatedAgainst({
  analysis,
  input,
}: {
  analysis: EventAnalysisDef;
  input: Record<string, string>;
}) {
  return (
    <div className="analysis-rated-against">
      {analysis.inputs.map((one) => (
        <p key={one.key} className="an-asked">
          {eventAnalysisRatedAgainst(one.label, input[one.key] ?? '')}
        </p>
      ))}
    </div>
  );
}

/** Where an attendee stands on the record: their status, tracked without one, or not tracked. */
const NOT_TRACKED = 'not_tracked';
const TRACKED = 'tracked';

export function attendeeStanding(attendee: EventAttendee): string {
  const record = attendee.on_record;
  if (!record?.tracked) return NOT_TRACKED;
  return record.status ?? TRACKED;
}

/**
 * **Who is coming, by status**: one segment per status in the vertical's
 * order, then the tracked people whose status the record did not give, then
 * everyone not tracked, hatched.
 */
export function standingSegments(vertical: string, standings: readonly string[]): ChartSegment[] {
  const count = (key: string) => standings.filter((one) => one === key).length;
  const order = statusesByTone(vertical);
  const statuses: ChartSegment[] = order.flatMap((status) => {
    const n = count(status);
    const label = subjectStatusLabel(vertical, status);
    return n > 0 && label ? [statusSegment(vertical, status, label, n)] : [];
  });
  const named = new Set(statuses.map((one) => one.key));
  const unnamed = standings.filter((one) => one !== NOT_TRACKED && !named.has(one)).length;
  return [
    ...statuses,
    ...(unnamed > 0 ? [{ key: TRACKED, label: EVENT_TRACKED, count: unnamed, tone: 'known' }] : []),
    { key: NOT_TRACKED, label: EVENT_NOT_TRACKED, count: count(NOT_TRACKED), swatch: 'hatch' },
  ];
}

/**
 * An analysis that is only its report (no factors, so no scan): who is coming
 * by status, from the attendee list and the record, its one-line conclusion,
 * then the report's first sections quoted, or the offer to write it, and
 * everyone by status behind a disclosure.
 */
function ReportAnalysisPanel({
  analysis,
  entityId,
}: {
  analysis: EventAnalysisDef;
  entityId: string;
}) {
  const vertical = useOrg().vertical;
  const reports = useReports();
  const latest = latestAnalysisReport(reports.data?.items ?? [], analysis, entityId);
  const whole = useWholeList(entityId, true);
  const [pick, setPick] = useState<{ open: boolean; key: string | null }>({
    open: false,
    key: null,
  });
  const [open, setOpen] = useState<string | null>(null);

  const standings = whole.rows.map(attendeeStanding);
  const segments = standingSegments(vertical, standings);
  const total = whole.list.data?.pages[0]?.counts.total ?? 0;
  const tracked = standings.filter((one) => one !== NOT_TRACKED).length;

  let summary;
  if (whole.list.isPending)
    summary = (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  else if (whole.list.isError) summary = <ErrorLine error={whole.list.error} load />;
  else if (total === 0) summary = <EmptyLine>{EVENT_ANALYSIS_NO_LIST}</EmptyLine>;
  else
    summary = (
      <>
        <figure className="an-fig">
          <Ring segments={segments} totalLabel={EVENT_ANALYSIS_TOTAL} />
          <figcaption className="an-cap">
            {standings.length > 0 ? (
              <p className="an-says">{eventTrackedConclusion(tracked, standings.length)}</p>
            ) : null}
            <Legend
              segments={segments}
              picked={pick.open ? pick.key : null}
              onPick={(key) => setPick({ open: true, key })}
            />
          </figcaption>
        </figure>
      </>
    );

  return (
    <section className="an-panel section analysis">
      <AnalysisHead
        analysis={analysis}
        entityId={entityId}
        latest={latest}
        reportsLoaded={reports.isSuccess}
      />
      {reports.isError ? <ErrorLine error={reports.error} load /> : null}
      {summary}
      {latest ? <ReportExcerpt analysis={analysis} item={latest} /> : null}
      {total > 0 && whole.rows.length > 0 ? (
        <details
          className="an-behind"
          open={pick.open}
          onToggle={(event) => {
            const now = (event.currentTarget as HTMLDetailsElement).open;
            if (now !== pick.open) setPick({ open: now, key: now ? pick.key : null });
          }}
        >
          <summary>
            {eventAttendeesDisclosure(standings.length)}
            {tracked > 0 ? (
              <span className="quiet"> · {eventDisclosureTracked(tracked)}</span>
            ) : null}
          </summary>
          {pick.open ? (
            <StandingGroups
              segments={segments}
              rows={whole.rows}
              standings={standings}
              picked={pick.key}
              onAll={() => setPick({ open: true, key: null })}
              onOpen={setOpen}
              vertical={vertical}
            />
          ) : null}
        </details>
      ) : null}
      {open ? (
        <AttendeeModal eventId={entityId} nodeId={open} onClose={() => setOpen(null)} />
      ) : null}
    </section>
  );
}

/** Everyone coming, by status, each with what the list gives. */
function StandingGroups({
  segments,
  rows,
  standings,
  picked,
  onAll,
  onOpen,
  vertical,
}: {
  segments: readonly ChartSegment[];
  rows: readonly EventAttendee[];
  standings: readonly string[];
  picked: string | null;
  onAll: () => void;
  onOpen: (nodeId: string) => void;
  vertical: string;
}) {
  const total = rows.length;
  const chosen = picked ? segments.find((one) => one.key === picked) : undefined;
  return (
    <>
      {chosen ? (
        <p className="an-filter-line">
          <span>{eventFilterLine(chosen.label, chosen.count, total)}</span>
          <button type="button" className="linkish flush" onClick={onAll}>
            {EVENT_SHOW_EVERY_GROUP}
          </button>
        </p>
      ) : null}
      {segments
        .filter((one) => !chosen || one.key === chosen.key)
        .map((segment) => {
          const members = rows.filter((_, at) => standings[at] === segment.key);
          return (
            <div className="tier-group" key={segment.key}>
              <div className="sub">
                <span
                  className={`an-sw ${segment.tone ? 'tone' : (segment.swatch ?? '')}`}
                  data-tone={segment.tone}
                />
                <h4>{segment.label}</h4>
                <span className="c num">{segment.count}</span>
              </div>
              {members.length === 0 ? (
                <p className="an-nobody">{EVENT_ANALYSIS_NOBODY}</p>
              ) : (
                <ul className="an-roll">
                  {members.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="person att-name"
                        onClick={() => onOpen(row.id)}
                      >
                        {row.name}
                      </button>
                      <span className="an-roll-meta">{attendeeDetails(row.identifiers)}</span>
                      {segment.tone && segment.key !== TRACKED ? (
                        <span className="an-st">
                          <StatusDot vertical={vertical} status={segment.key as SubjectStatus} />
                          {segment.label}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
    </>
  );
}

/** The new-report form for an analysis's report, with the event (and its account) already chosen. */
export function newAnalysisReportPath(
  analysis: EventAnalysisDef,
  entityId: string,
  accountId: string | null,
): string {
  return `/reports/new?${new URLSearchParams({
    type: analysis.report,
    event: entityId,
    ...(accountId ? { account: accountId } : {}),
  }).toString()}`;
}

/** The analysis's newest live report on this event; an archived one no longer stands for it. */
export function latestAnalysisReport(
  items: readonly ReportListItem[],
  analysis: EventAnalysisDef,
  entityId: string,
): ReportListItem | undefined {
  return items
    .filter(
      (item) =>
        item.state === 'active' &&
        item.type === analysis.report &&
        item.event?.entity_id === entityId,
    )
    .sort((a, b) =>
      (b.last_version_at ?? b.created_at).localeCompare(a.last_version_at ?? a.created_at),
    )[0];
}
